// Public candidate portal — accessed via /candidate/:token, no login.
// The candidate sees their pipeline status, interview rounds, and document
// requirements; they can upload mandatory documents directly here.

import React, { useEffect, useState } from "react";
import { K } from "../theme";
import { CheckCircle2, Upload, Clock, AlertCircle, FileText, ShieldCheck } from "lucide-react";
import { BASE } from "../api";

const BGV_FIELDS = [
  ["last_company_name", "Last Company Name", "text"],
  ["last_company_joining_date", "Joining Date (at last company)", "date"],
  ["last_working_date", "Last Working Date", "date"],
  ["last_designation", "Last Designation", "text"],
  ["total_experience", "Total Experience", "text"],
  ["last_salary_inhand", "Last Salary — In-hand", "text"],
  ["last_salary_ctc", "Last Salary — CTC", "text"],
  ["expected_salary_inhand", "Expected Salary — In-hand", "text"],
  ["expected_salary_ctc", "Expected Salary — CTC", "text"],
  ["notice_period", "Notice Period", "text"],
  ["current_location", "Current Location", "text"],
  ["reporting_manager_name", "Reporting Manager Name", "text"],
  ["last_hr_name", "Last Company HR — Name", "text"],
  ["last_hr_email", "Last Company HR — Official Email", "email"],
  ["last_hr_contact", "Last Company HR — Contact Number", "text"],
  ["reason_for_leaving", "Reason for Leaving Last Company", "text"],
  ["uan", "UAN (if any)", "text"],
  ["linkedin_id", "LinkedIn Id", "text"],
];

// Everything on this form is required except the ones that say "if any" / are
// optional by nature, plus the joining date at the last company and the last
// working date. Mirrored on the server (candidate-portal.js).
const BGV_OPTIONAL = ["uan", "linkedin_id", "last_company_joining_date", "last_working_date"];
const BGV_REQUIRED = BGV_FIELDS.map(([k]) => k).filter(k => !BGV_OPTIONAL.includes(k));

const STAGE_DESC = {
  New: "Your application has been received and is under review.",
  Interviewing: "Your interviews are in progress — please check the schedule below.",
  Offer: "An offer has been extended. Please review the details below.",
  Hired: "Welcome aboard! We'll see you on your join date.",
  Rejected: "Thank you for your interest — we have decided to move ahead with other candidates.",
};

const STAGE_COLOR = {
  New: K.t3, Interviewing: K.bl, Offer: K.a, Hired: K.g, Rejected: K.r,
};

export const CandidatePortal = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [msg, setMsg] = useState(null);
  const [bgvForm, setBgvForm] = useState({});
  const [savingBgv, setSavingBgv] = useState(false);

  const load = () => {
    setLoading(true);
    fetch(`${BASE}/api/candidate-portal/${token}`)
      .then(r => r.json().then(j => ({ ok: r.ok, j })))
      .then(({ ok, j }) => { if (ok) { setData(j); setBgvForm(j.bgvDetails || {}); setErr(null); } else setErr(j.error || "Failed to load"); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(load, [token]);

  const flash = (text, kind = "ok") => { setMsg({ text, kind }); setTimeout(() => setMsg(null), 3000); };

  const upload = async (docId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return flash("File too large (max 10MB)", "err");
    setUploading(docId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${BASE}/api/candidate-portal/${token}/upload/${docId}`, { method: "POST", body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Upload failed");
      flash(`${j.docType} uploaded`);
      load();
    } catch (e) { flash(e.message, "err"); } finally { setUploading(null); }
  };

  const submitBgv = async () => {
    setSavingBgv(true);
    try {
      const r = await fetch(`${BASE}/api/candidate-portal/${token}/bgv`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bgvForm),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Failed to submit");
      flash("Details submitted — thank you!");
      load();
    } catch (e) { flash(e.message, "err"); } finally { setSavingBgv(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-bg text-t3 text-[15px]">Loading…</div>;
  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-4">
      <div className="bg-bg1 border border-rd rounded-2xl p-6 max-w-md text-center">
        <AlertCircle size={32} color={K.r} className="mx-auto mb-2" />
        <div className="text-[15px] font-bold text-t0 mb-1">Link unavailable</div>
        <div className="text-[14px] text-t3">{err}</div>
        <div className="text-[14px] text-t3 mt-3">If you believe this is an error, please contact the recruiter who shared this link with you.</div>
      </div>
    </div>
  );

  const stageColor = STAGE_COLOR[data.stage] || K.t3;
  const stageDesc = STAGE_DESC[data.stage] || "";
  const allMandatoryUploaded = data.documents.filter(d => d.mandatory).every(d => d.uploaded);

  return (
    <div className="min-h-screen bg-bg p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-bg1 border border-b1 rounded-2xl p-5 mb-4">
          <div className="text-[14px] text-t3 uppercase tracking-wider">Candidate Portal</div>
          <div className="text-[20px] font-extrabold text-t0 mt-1">Hi {data.name},</div>
          <div className="text-[14px] text-t2 mt-1">Position: <span className="font-semibold">{data.position}</span></div>
        </div>

        {msg && <div className={`px-3 py-2 mb-3 rounded-lg text-[14px] font-semibold ${msg.kind === "err" ? "bg-rb text-r border border-rd" : "bg-gb text-g border border-gd"}`}>{msg.text}</div>}

        {/* Stage card */}
        <div className="bg-bg1 border border-b1 rounded-2xl p-5 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[14px] text-t3 uppercase tracking-wider">Current Status</div>
            <div className="px-[10px] py-[2px] rounded-pill text-[14px] font-semibold uppercase" style={{ background: stageColor + "14", color: stageColor, border: `1px solid ${stageColor}22` }}>{data.stage}</div>
          </div>
          <div className="text-[15px] text-t1">{stageDesc}</div>
          {(data.stage === "Offer" || data.stage === "Hired") && data.offerCtc && (
            <div className="grid grid-cols-3 gap-2 mt-3 text-[14px]">
              <div className="bg-bg2 rounded-lg p-2 border border-b0"><div className="text-[12px] text-t3 uppercase">Offer CTC</div><div className="font-semibold mt-[1px]">₹{(data.offerCtc / 100000).toFixed(1)}L / yr</div></div>
              <div className="bg-bg2 rounded-lg p-2 border border-b0"><div className="text-[12px] text-t3 uppercase">Offer Date</div><div className="font-semibold mt-[1px]">{data.offerDate || "—"}</div></div>
              <div className="bg-bg2 rounded-lg p-2 border border-b0"><div className="text-[12px] text-t3 uppercase">Join Date</div><div className="font-semibold mt-[1px]">{data.joinDate || "—"}</div></div>
            </div>
          )}
        </div>

        {/* Background verification details */}
        <div className="bg-bg1 border border-b1 rounded-2xl p-5 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[14px] text-t3 uppercase tracking-wider">Background Verification Details</div>
            {data.bgvSubmitted && <div className="text-[14px] font-semibold text-g flex items-center gap-1"><ShieldCheck size={12} />Submitted</div>}
          </div>
          {data.bgvSubmitted ? (
            <div className="text-[14px] text-t3">Thank you — your details have been submitted. If anything needs correcting, please contact your recruiter.</div>
          ) : (
            <>
              <div className="text-[14px] text-t3 mb-3">We need a few details from your last employment for background verification. Fill in everything below, upload the documents further down, then press Submit at the very end.</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                {BGV_FIELDS.map(([key, label, type]) => (
                  <div key={key}>
                    <div className="text-[12px] text-t3 mb-1">{label}</div>
                    <input
                      type={type}
                      value={bgvForm[key] || ""}
                      onChange={e => setBgvForm(f => ({ ...f, [key]: e.target.value }))}
                      className="w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none"
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Rounds */}
        {data.rounds.length > 0 && (
          <div className="bg-bg1 border border-b1 rounded-2xl p-5 mb-4">
            <div className="text-[14px] text-t3 uppercase tracking-wider mb-3">Interview Schedule</div>
            <div className="space-y-2">
              {data.rounds.map((r, i) => {
                const sched = r.scheduledAt ? new Date(r.scheduledAt) : null;
                const passed = r.outcome === "Pass";
                const failed = r.outcome === "Fail";
                const pending = r.outcome === "Pending" || r.outcome === "Hold";
                const color = passed ? K.g : failed ? K.r : K.bl;
                const Icon = passed ? CheckCircle2 : failed ? AlertCircle : Clock;
                return (
                  <div key={i} className="flex items-center gap-3 p-3 bg-bg2 rounded-lg border border-b0">
                    <Icon size={18} color={color} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold text-t0">{r.name}</div>
                      <div className="text-[14px] text-t3">
                        {sched ? sched.toLocaleString('en-IN') : "Time TBD"}{r.interviewMode ? ` · ${r.interviewMode}` : ""}
                        {r.interviewMode === "Virtual" && r.virtualLink && <> · <a href={r.virtualLink} target="_blank" rel="noreferrer" className="text-p font-semibold no-underline">Join Link</a></>}
                      </div>
                    </div>
                    <div className="text-[14px] font-semibold uppercase px-[8px] py-[2px] rounded-pill" style={{ background: color + "14", color, border: `1px solid ${color}22` }}>{pending ? "Pending" : passed ? "Selected" : failed ? "Not Selected" : r.outcome}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Documents */}
        <div className="bg-bg1 border border-b1 rounded-2xl p-5 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[14px] text-t3 uppercase tracking-wider">Documents</div>
            {allMandatoryUploaded && <div className="text-[14px] font-semibold text-g flex items-center gap-1"><CheckCircle2 size={12} />All required uploaded</div>}
          </div>
          <div className="text-[14px] text-t3 mb-3">Please upload the documents below. PDF, JPG, or PNG. Max 10 MB each.</div>
          <div className="space-y-2">
            {data.documents.map(d => (
              <div key={d.id} className="flex items-center gap-3 p-3 bg-bg2 rounded-lg border border-b0">
                <FileText size={18} color={d.verified ? K.g : d.uploaded ? K.bl : (d.mandatory ? K.a : K.t3)} />
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-t0">{d.docType}{d.mandatory && <span className="text-r ml-1">*</span>}</div>
                  <div className="text-[14px] text-t3">
                    {d.verified ? "Verified" : d.uploaded ? "Uploaded — pending verification" : (d.mandatory ? "Required" : "Optional")}
                  </div>
                </div>
                {!d.verified && (
                  <label className="cursor-pointer flex items-center gap-1 px-3 py-1 rounded-lg text-[14px] font-semibold" style={{ background: K.p + "14", color: K.p, border: `1px solid ${K.p}22` }}>
                    <Upload size={12} />
                    {uploading === d.id ? "Uploading…" : (d.uploaded ? "Replace" : "Upload")}
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => upload(d.id, e.target.files[0])} disabled={uploading === d.id} />
                  </label>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* The one submit for the whole form — last, after details and documents. */}
        {!data.bgvSubmitted && (() => {
          const missingBgv = BGV_REQUIRED.filter(k => !String(bgvForm[k] || "").trim());
          const docsOk = allMandatoryUploaded;
          const ready = missingBgv.length === 0 && docsOk && !savingBgv;
          const labelFor = Object.fromEntries(BGV_FIELDS.map(([k, l]) => [k, l]));
          return (
            <div className="bg-bg1 border border-b1 rounded-2xl p-5 mb-4">
              {!ready && (
                <div className="text-[14px] text-a mb-2">
                  {missingBgv.length > 0 && <>Missing details: {missingBgv.map(k => labelFor[k]).join(", ")}. </>}
                  {!docsOk && <>Upload the required documents first.</>}
                </div>
              )}
              <button
                onClick={submitBgv}
                disabled={!ready}
                className="px-4 py-2 rounded-lg text-[14px] font-semibold text-white border-0"
                style={{ background: K.p, cursor: ready ? "pointer" : "not-allowed", opacity: ready ? 1 : 0.5 }}
              >
                {savingBgv ? "Submitting…" : "Submit"}
              </button>
            </div>
          );
        })()}

        <div className="text-[14px] text-t3 text-center mt-4">Questions? Reply to the email from your recruiter.</div>
      </div>
    </div>
  );
};
